"use strict"

import React, { useState, useEffect } from "react"
import { Tabs, Tab, Modal } from "react-bootstrap"
import Prism from "prismjs"

import LoadingIndicator from "../../resources/LoadingIndicator"
import { getObjectsList } from "../../../../Loaders"
import { logToServer } from "../../../../ErrorBoundary"
import { isNumber } from "../../../utils"
import {
  isLTPM,
  injectIntl,
  withApplicationConfig,
} from "../../../../components/shared_components_registration"

// Where we set distinctions for LTPM/LL versions of the component
const registration = {
  no_computations_msg: (intl) =>
    isLTPM ? intl.formatMessage({ id: "graphing.no.computations.message" }) : "No Computations For This Tag",
  dependencies_label: (intl) =>
    isLTPM
      ? intl.formatMessage({ id: "graphing.dependencies.label", defaultMessage: "Calculation Dependencies" })
      : "Calculation Dependencies",
  tag_units_label: (intl) =>
    isLTPM
      ? `(${intl.formatMessage({ id: "general.tag" })}: ${intl.formatMessage({ id: "general.units" })})`
      : "(tag: units)",
  settings_label: (intl) =>
    isLTPM ? intl.formatMessage({ id: "general.settings", defaultMessage: "Settings" }) : "Settings",
  load_tag_definitions_url: (tag) =>
    isLTPM ? `/warehouse/tags/${tag}/definitions` : `/warehouse/querytags/${tag}/definitions`,
  window_before_label: (intl) => "Averaging Window Before (Hours)", // TODO - Get translation?
  window_after_label: (intl) => "Averaging Window After (Hours)", // TODO - Get translation?
  sampling_rate_label: (intl) => "Sampling Rate (Minutes)", // TODO - Get translation?
  as_often_as_possible: (intl) => "As Often As Possible", // TODO - Get translation?
}

function TagDefinitionsPopper({
  tag,
  title,
  tagData = {},
  config = {}, // From withApplicationConfig - Currently only used for LTPM
}) {
  const [show, setShow] = useState(false)
  const [showCalcModal, setShowCalcModal] = useState(true)
  useEffect(() => {
    if (isLTPM && config?.user?.permissions) {
      // Handle use case on LTPM where certain user types shouldn't be able to see calculation details
      setShowCalcModal(config.user.permissions.includes("view_calculations_modal"))
    }
  }, [config])

  const onClick = (ev) => {
    ev.stopPropagation()
    ev.preventDefault()
    setShow(true)
  }

  const onHide = () => {
    setShow(false)
  }

  return (
    <React.Fragment>
      <Modal size={"lg"} dialogClassName={"definitions-modal"} show={show} onHide={onHide}>
        <Modal.Header closeButton>
          <Modal.Title>{title || tag}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{tagData.description}</p>
          <TagDefinitions tag={tag} />
        </Modal.Body>
      </Modal>
      {showCalcModal && (
        <a href={"#"} onClick={onClick}>
          <span className={"fa fa-info-circle"} aria-hidden={"true"} />
        </a>
      )}
    </React.Fragment>
  )
}

export default withApplicationConfig(TagDefinitionsPopper)

const DependencyColumn = ({ arr = [], col_size }) => (
  <div className={`col-${col_size}`}>
    {arr.map((dep, idx) => (
      <pre style={{ marginBottom: "0" }} key={`col${idx}_${dep.tag}`}>
        {dep.tag}: <em>({dep.units})</em>
      </pre>
    ))}
  </div>
)

const _TagDefinitions = ({ tag, intl }) => {
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(undefined)
  const [tagDefinitions, setTagDefinitions] = useState([])

  const firstArrayElements = (arr) => {
    return arr.slice(0, Math.ceil(arr.length / 2))
  }

  const lastArrayElements = (arr) => {
    return arr.slice(Math.ceil(arr.length / 2))
  }

  const loadDefs = (tag) => {
    setLoading(true)
    setErr(undefined)

    const url = registration.load_tag_definitions_url(tag)
    getObjectsList(url)
      .then((tagDefs) => {
        setLoading(false)
        setErr(undefined)
        setTagDefinitions(tagDefs)
      })
      .catch((err) => {
        console.error(err)
        logToServer(err)
        setLoading(false)
        setErr(err)
        setTagDefinitions([])
      })
  }

  useEffect(() => {
    loadDefs(tag)
  }, [])

  if (loading) {
    return <LoadingIndicator />
  } else if (err) {
    return <div className={"error-message"}>{err.toString()}</div>
  } else {
    if (tagDefinitions && tagDefinitions.length > 0) {
      return (
        <Tabs id={"code-tabs"} defaultActiveKey={0}>
          {tagDefinitions.map((tagDef, idx) => (
            <Tab key={idx} eventKey={idx} title={tagDef.name}>
              <div className={"prism-code-pre"} style={{ marginTop: "10px" }}>
                {/*Headers */}
                <div className={"row"} style={{ marginBottom: "10px" }}>
                  <div className={"col-6"}>
                    <b>{registration.dependencies_label(intl)}</b> {registration.tag_units_label(intl)}
                  </div>
                  <div className={"col-6"}>
                    <b>{registration.settings_label(intl)}</b>
                  </div>
                </div>
                <div className={"row"}>
                  {/*Dependencies*/}
                  {tagDef.dependencies.length === 0 ? (
                    <div className={"col-6"} style={{ paddingBottom: "1.0em" }}>
                      <span>-</span>
                    </div>
                  ) : (
                    <>
                      <DependencyColumn arr={firstArrayElements(tagDef.dependencies)} col_size={3} />
                      <DependencyColumn arr={lastArrayElements(tagDef.dependencies)} col_size={3} />
                    </>
                  )}
                  {/*Settings */}
                  <div className={"col-6"}>
                    {isNumber(tagDef?.settings?.window_hours_before) && (
                      <div>
                        <pre style={{ marginBottom: "0" }}>
                          {registration.window_before_label(intl)}:&nbsp;
                          {tagDef.settings.window_hours_before}
                        </pre>
                      </div>
                    )}
                    {isNumber(tagDef?.settings?.window_hours_after) && (
                      <div>
                        <pre style={{ marginBottom: "0" }}>
                          {registration.window_after_label(intl)}:&nbsp;
                          {tagDef.settings.window_hours_after}
                        </pre>
                      </div>
                    )}
                    {isNumber(tagDef?.settings?.sampling_rate_minutes) && (
                      <div>
                        <pre style={{ marginBottom: "0" }}>
                          {registration.sampling_rate_label(intl)}:&nbsp;
                          {tagDef.settings.sampling_rate_minutes === 0
                            ? registration.as_often_as_possible(intl)
                            : tagDef.settings.sampling_rate_minutes}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <TagDefinitionCode language={tagDef.language} source={tagDef.source} />
              {!isLTPM && (
                <div className={"prism-code-pre"}>
                  <div className={"row"} style={{ margin: "0", marginBottom: "10px" }}>
                    <b>Applicable Sites & Systems</b>:
                  </div>
                  <pre>
                    <code>{JSON.stringify(tagDef.systems, null, 4)}</code>
                  </pre>
                </div>
              )}
            </Tab>
          ))}
        </Tabs>
      )
    } else {
      return <div>{registration.no_computations_msg(intl)}</div>
    }
  }
}

const TagDefinitions = injectIntl(_TagDefinitions) // Makes intl available for translations

const TagDefinitionCode = ({ language, source }) => {
  const languageFormats = {
    python: {
      grammar: Prism.languages.python,
      language: "python",
    },
  }
  const format = languageFormats[language]

  if (format) {
    // Directly setting innerHTML is reasonable here because it's fundamentally equivalent
    // to what PrismJS would do if we ran it outside of a React component.
    //
    // Additionally, the input to this isn't really "untrusted", since it will have been
    // pulled from the server-side code-base.

    return (
      <pre className={"prism-code-pre"}>
        <code
          dangerouslySetInnerHTML={{
            __html: Prism.highlight(source, format.grammar, format.language),
          }}
        />
      </pre>
    )
  } else {
    return (
      <pre className={"prism-code-pre"}>
        <code>{source}</code>
      </pre>
    )
  }
}
